import { useState } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { useInteractions, useDeleteInteraction } from '../api';
import { INTERACTION_TYPES, formatInteractionType } from '../interactionTypes';
import InteractionEditForm from '../components/InteractionEditForm';

const PAGE = 50;

function nextDay(d: string): string {
  const dt = new Date(`${d}T00:00:00`);
  dt.setDate(dt.getDate() + 1);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}

export default function InteractionsList() {
  const [q, setQ] = useState('');
  const [type, setType] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [offset, setOffset] = useState(0);

  const { data, isFetching } = useInteractions({
    q,
    type,
    occurred_after: from,
    // occurred_before is exclusive; bump one day so the picked To date is included.
    occurred_before: to ? nextDay(to) : '',
    limit: PAGE,
    offset,
  });
  const deleteInteraction = useDeleteInteraction();
  const [editingId, setEditingId] = useState<string | null>(null);
  const rows = data?.interactions ?? [];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-0.5 text-xs text-slate-600 dark:text-slate-400">
          <span>Notes contain</span>
          <input
            value={q}
            onChange={(e) => {
              setOffset(0);
              setQ(e.target.value);
            }}
            className="rounded border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm"
          />
        </label>
        <label className="flex flex-col gap-0.5 text-xs text-slate-600 dark:text-slate-400">
          <span>Type</span>
          <select
            value={type}
            onChange={(e) => {
              setOffset(0);
              setType(e.target.value);
            }}
            className="rounded border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm"
          >
            <option value="">All</option>
            {INTERACTION_TYPES.map((t) => (
              <option key={t} value={t}>{formatInteractionType(t)}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-0.5 text-xs text-slate-600 dark:text-slate-400">
          <span>From</span>
          <input
            type="date"
            value={from}
            onChange={(e) => {
              setOffset(0);
              setFrom(e.target.value);
            }}
            className="rounded border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm"
          />
        </label>
        <label className="flex flex-col gap-0.5 text-xs text-slate-600 dark:text-slate-400">
          <span>To</span>
          <input
            type="date"
            value={to}
            onChange={(e) => {
              setOffset(0);
              setTo(e.target.value);
            }}
            className="rounded border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm"
          />
        </label>
        <span className="ml-auto text-xs text-slate-500 dark:text-slate-400">
          {isFetching ? 'Loading…' : `Showing ${rows.length} from offset ${offset}`}
        </span>
      </div>

      <ul className="divide-y divide-slate-100 dark:divide-slate-800 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
        {rows.map((i) => (
          <li key={i.id} className="px-3 py-2 text-sm">
            {editingId === i.id ? (
              <InteractionEditForm
                interaction={i}
                onDone={() => setEditingId(null)}
                onCancel={() => setEditingId(null)}
              />
            ) : (
            <>
            <div className="flex justify-between gap-2">
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                {i.people?.map((p) => (
                  <Link
                    key={p.id}
                    className="font-semibold text-slate-800 dark:text-slate-200 hover:underline"
                    to={`/people/${p.id}`}
                  >
                    {p.name_full}
                  </Link>
                ))}
                {i.households?.map((h) => (
                  <Link
                    key={h.id}
                    className="font-semibold text-slate-800 dark:text-slate-200 hover:underline"
                    to={`/households/${h.id}`}
                  >
                    {h.name_directory}
                  </Link>
                ))}
                {!i.people?.length && !i.households?.length && (
                  <span className="font-semibold text-slate-500 dark:text-slate-400">General note</span>
                )}
                <span className="rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-xs text-slate-600 dark:text-slate-400">
                  {formatInteractionType(i.type)}
                </span>
                {i.performed_by && (
                  <span className="text-xs text-slate-500 dark:text-slate-400">by {i.performed_by}</span>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-2 self-start">
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  {new Date(i.occurred_at).toLocaleString()}
                </span>
                <button
                  onClick={() => setEditingId(i.id)}
                  className="text-xs text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                >
                  Edit
                </button>
                <button
                  onClick={() => { if (confirm('Delete this interaction?')) deleteInteraction.mutate(i.id); }}
                  className="text-xs text-slate-400 dark:text-slate-500 hover:text-red-600 dark:hover:text-red-400"
                >
                  Delete
                </button>
              </div>
            </div>
            {i.notes && (
              <div className="prose dark:prose-invert prose-sm mt-1 max-w-none text-slate-700 dark:text-slate-300">
                <ReactMarkdown>{i.notes}</ReactMarkdown>
              </div>
            )}
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-400 dark:text-slate-500">
              <span className="ml-auto">recorded by {i.created_by}</span>
            </div>
            </>
            )}
          </li>
        ))}
        {rows.length === 0 && !isFetching && (
          <li className="px-3 py-2 text-sm text-slate-500 dark:text-slate-400">No interactions match.</li>
        )}
      </ul>

      <div className="flex justify-between text-sm">
        <button
          disabled={offset === 0}
          onClick={() => setOffset(Math.max(0, offset - PAGE))}
          className="rounded border border-slate-300 dark:border-slate-600 px-3 py-1 disabled:opacity-50"
        >
          Previous
        </button>
        <button
          disabled={rows.length < PAGE}
          onClick={() => setOffset(offset + PAGE)}
          className="rounded border border-slate-300 dark:border-slate-600 px-3 py-1 disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
}
